import { useState, useEffect, useRef, useCallback } from 'react';
import { useInstitutionalTrading } from './useInstitutionalTrading';

export const useSmartHedgeStrategy = (position) => {
  const { btcPrice, strikes } = useInstitutionalTrading();
  const [hedgeStrategy, setHedgeStrategy] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Hybrid approach parameters
  const DEBOUNCE_SECONDS = 5; // Update every 5 seconds max
  const PRICE_THRESHOLD = 50;  // Update if price moves >$50
  const CALCULATION_DELAY = 800; // Smooth calculation time
  
  // Refs for tracking state
  const lastPriceRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  const pendingTimerRef = useRef(null);
  const isCalculatingRef = useRef(false);

  // Memoized strategy calculation function
  const calculateStrategyData = useCallback((currentPrice, currentStrikes) => {
    if (!currentPrice || !currentStrikes || currentStrikes.length === 0 || !position) {
      return null;
    }

    const atmStrike = currentStrikes.find(s => s.call.moneyness === 'ATM');
    if (!atmStrike) return null;

    const strategy = {
      strategy_name: 'Protective Put',
      description: 'Purchase put options to protect against downside risk',
      risk_level: 'Low',
      strike_price: atmStrike.price,
      cost: atmStrike.put.premium * position.size * 10,
      max_protection: (atmStrike.price - position.entry_price) * position.size,
      expiry: '2HR',
      moneyness: '0.2',
      risk_reduction: 85,
      remaining_risk: position.unrealized_pnl * 0.15,
      last_updated: Date.now(),
      price_basis: currentPrice
    };

    return strategy;
  }, [position]);

  // Smooth strategy update with transitions
  const updateStrategySmooth = useCallback(async (newStrategy) => {
    if (isCalculatingRef.current) return; // Prevent overlapping updates
    
    isCalculatingRef.current = true;
    setIsCalculating(true);
    setIsTransitioning(true);

    console.log('🛡️ SMOOTH UPDATE: Starting strategy transition...');

    // Smooth transition delay
    await new Promise(resolve => setTimeout(resolve, CALCULATION_DELAY));

    setHedgeStrategy(newStrategy);
    setIsTransitioning(false);
    setIsCalculating(false);
    isCalculatingRef.current = false;

    console.log('🛡️ SMOOTH UPDATE: Strategy updated smoothly');
  }, []);

  // Hybrid trigger logic - smart updates
  const triggerStrategyUpdate = useCallback((currentPrice) => {
    const currentTime = Date.now();
    const strategy = calculateStrategyData(currentPrice, strikes);
    
    if (!strategy) return;

    // Clear any pending timer
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    // Update refs
    lastPriceRef.current = currentPrice;
    lastUpdateTimeRef.current = currentTime;

    // Trigger smooth update
    updateStrategySmooth(strategy);
  }, [strikes, calculateStrategyData, updateStrategySmooth]);

  // Hybrid approach: Price change monitoring
  useEffect(() => {
    if (!btcPrice || !strikes || strikes.length === 0 || !position) return;

    const currentTime = Date.now();
    const lastPrice = lastPriceRef.current;
    const lastUpdateTime = lastUpdateTimeRef.current;

    // First time setup
    if (lastPrice === null) {
      triggerStrategyUpdate(btcPrice);
      return;
    }

    const priceChange = Math.abs(btcPrice - lastPrice);
    const timeSinceLastUpdate = (currentTime - lastUpdateTime) / 1000; // Convert to seconds

    console.log('🔄 HYBRID MONITOR:', {
      priceChange: priceChange.toFixed(2),
      threshold: PRICE_THRESHOLD,
      timeSince: timeSinceLastUpdate.toFixed(1),
      debounceTime: DEBOUNCE_SECONDS
    });

    // HYBRID LOGIC: Update if price threshold exceeded OR debounce time passed
    if (priceChange >= PRICE_THRESHOLD) {
      console.log('🚨 THRESHOLD TRIGGER: Price moved $' + priceChange.toFixed(2));
      triggerStrategyUpdate(btcPrice);
    } else if (timeSinceLastUpdate >= DEBOUNCE_SECONDS) {
      console.log('⏰ DEBOUNCE TRIGGER: ' + DEBOUNCE_SECONDS + 's passed');
      triggerStrategyUpdate(btcPrice);
    } else {
      // Schedule update for remaining debounce time
      const remainingTime = (DEBOUNCE_SECONDS - timeSinceLastUpdate) * 1000;
      
      if (!pendingTimerRef.current && remainingTime > 0) {
        console.log('⏳ SCHEDULING UPDATE in ' + (remainingTime/1000).toFixed(1) + 's');
        pendingTimerRef.current = setTimeout(() => {
          triggerStrategyUpdate(btcPrice);
          pendingTimerRef.current = null;
        }, remainingTime);
      }
    }
  }, [btcPrice, strikes, position, triggerStrategyUpdate]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
    };
  }, []);

  return { 
    hedgeStrategy, 
    isCalculating, 
    isTransitioning,
    // Debug info
    debugInfo: {
      lastPrice: lastPriceRef.current,
      lastUpdate: lastUpdateTimeRef.current,
      priceThreshold: PRICE_THRESHOLD,
      debounceSeconds: DEBOUNCE_SECONDS
    }
  };
};
